import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCustomerDetails, addLead } from '../../api';
import DashboardNav from '../../components/dashboard/DashboardNav';
import PvConsumptionQuestions from '../../components/forms/PvConsumptionQuestions';
import {
  hasQualifyingPvConsumption,
  isPvConsumptionStepComplete,
  isPvConsumptionDisqualified,
  getPvConsumptionSubmitValues,
  isYearlyConsumptionOver2700Valid
} from '../../utils/pvConsumptionQualification';

function AddPvLeadToCustomer({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('customerId');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [customer, setCustomer] = useState(null);
  const [customerLoading, setCustomerLoading] = useState(true);

  // PV-Daten State
  const [propertyData, setPropertyData] = useState({
    isOwner: null,
    buildingType: '',
    roofOrientation: '',
    roofAreaOver20: null,
    roofAreaSize: '',
    hasAlternativeMounting: null,
    alternativeMountingArea: '',
    hasMonumentProtection: null,
    consumptionOver2700: null,
    consumptionInRangeWith3Months: null,
    hasRealizationTimeframe: null,
    hasFutureEnergyConsumers: null,
    yearlyConsumption: '',
  });

  const [progression, setProgression] = useState(1);

  // Kundendaten laden, wenn customerId vorhanden
  useEffect(() => {
    if (customerId && user?.id) {
      const fetchCustomer = async () => {
        try {
          const data = await getCustomerDetails(user.id, customerId);
          if (data.success) {
            setCustomer(data.customer);
          } else {
            setError('Kunde nicht gefunden');
          }
        } catch (err) {
          setError('Fehler beim Laden der Kundendaten: ' + err.message);
        } finally {
          setCustomerLoading(false);
        }
      };
      fetchCustomer();
    } else {
      setCustomerLoading(false);
    }
  }, [customerId, user]);

  // Handler-Funktionen
  const handleOwnershipChange = (isOwner) => {
    setPropertyData({
      ...propertyData,
      isOwner: isOwner
    });
    if (progression === 1) {
      setProgression(2);
    }
  };

  const handleBuildingTypeChange = (type) => {
    setPropertyData({
      ...propertyData,
      buildingType: type
    });
    if (progression === 2) {
      setProgression(3);
    }
  };

  const handleRoofOrientationChange = (orientation) => {
    setPropertyData({
      ...propertyData,
      roofOrientation: orientation
    });
    if (progression === 3) {
      setProgression(4);
    }
  };

  const handleRoofAreaOver20Change = (isOver20) => {
    setPropertyData({
      ...propertyData,
      roofAreaOver20: isOver20,
      roofAreaSize: '',
      hasAlternativeMounting: null,
      alternativeMountingArea: ''
    });
    if (isOver20) {
      setProgression(5);
    } else {
      setProgression(6);
    }
  };

  const handleRoofAreaSizeChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      roofAreaSize: value
    });
    if (progression === 5 || progression === 6) {
      if (value.trim() !== '' && Number(value) >= 20) {
        setProgression(8);
      } else if (value.trim() !== '' && Number(value) < 20) {
        setProgression(6);
      }
    }
  };

  const handleAlternativeMountingChange = (hasAlternative) => {
    setPropertyData({
      ...propertyData,
      hasAlternativeMounting: hasAlternative,
      alternativeMountingArea: hasAlternative ? propertyData.alternativeMountingArea : ''
    });
    if (hasAlternative) {
      setProgression(7);
    } else {
      setProgression(8);
    }
  };

  const handleAlternativeMountingAreaChange = (e) => {
    const { value } = e.target;
    setPropertyData({
      ...propertyData,
      alternativeMountingArea: value
    });
    if (progression === 7 && value.trim() !== '') {
      if (Number(value) >= 20) {
        setProgression(8);
      }
    }
  };

  const handleMonumentProtectionChange = (hasProtection) => {
    setPropertyData({
      ...propertyData,
      hasMonumentProtection: hasProtection
    });
    if (progression === 8) {
      setProgression(9);
    }
  };

  const handleConsumptionOver2700Change = (isOver2700) => {
    const nextData = {
      ...propertyData,
      consumptionOver2700: isOver2700,
      consumptionInRangeWith3Months: isOver2700 ? null : propertyData.consumptionInRangeWith3Months,
      hasRealizationTimeframe: isOver2700 ? propertyData.hasRealizationTimeframe : null,
      yearlyConsumption: isOver2700 ? propertyData.yearlyConsumption : ''
    };
    setPropertyData(nextData);
    if (isOver2700) {
      setProgression(isYearlyConsumptionOver2700Valid(nextData.yearlyConsumption) ? 12 : 9);
    } else {
      setProgression(10);
    }
  };

  const handleYearlyConsumptionChange = (value) => {
    setPropertyData({
      ...propertyData,
      yearlyConsumption: value
    });
    if (propertyData.consumptionOver2700 === true && isYearlyConsumptionOver2700Valid(value)) {
      setProgression(propertyData.hasRealizationTimeframe !== null ? 13 : 12);
    }
  };

  const handleConsumptionInRangeWith3MonthsChange = (value) => {
    setPropertyData({
      ...propertyData,
      consumptionInRangeWith3Months: value
    });
    setProgression(value ? 11 : 10);
  };

  const handleRealizationTimeframeChange = (hasTimeframe) => {
    setPropertyData({
      ...propertyData,
      hasRealizationTimeframe: hasTimeframe
    });
    if (progression === 12) {
      setProgression(13);
    }
  };

  // Funktion zur Überprüfung der effektiven Dachfläche
  const getEffectiveRoofArea = () => {
    if (propertyData.roofAreaOver20 === true && propertyData.roofAreaSize) {
      return Number(propertyData.roofAreaSize);
    }
    if (propertyData.roofAreaOver20 === false &&
      propertyData.hasAlternativeMounting === true &&
      propertyData.alternativeMountingArea) {
      return Number(propertyData.alternativeMountingArea);
    }
    return 0;
  };

  // Prüfen, ob alle Pflichtfelder ausgefüllt sind
  const isStep1Valid = () => {
    const effectiveArea = getEffectiveRoofArea();

    return (
      propertyData.isOwner !== null &&
      propertyData.buildingType !== '' &&
      propertyData.roofOrientation !== '' &&
      propertyData.roofAreaOver20 !== null &&
      (
        (propertyData.roofAreaOver20 === true && effectiveArea >= 20) ||
        (propertyData.roofAreaOver20 === false &&
          propertyData.hasAlternativeMounting === true &&
          effectiveArea >= 20) ||
        (propertyData.roofAreaOver20 === false &&
          propertyData.hasAlternativeMounting === false)
      ) &&
      propertyData.hasMonumentProtection !== null &&
      isPvConsumptionStepComplete(propertyData)
    );
  };

  // Prüfen, ob Lead qualifiziert ist
  const isLeadQualified = () => {
    const effectiveArea = getEffectiveRoofArea();

    return (
      propertyData.isOwner === true &&
      propertyData.buildingType !== '' &&
      propertyData.roofOrientation !== 'Nord' &&
      effectiveArea >= 20 &&
      propertyData.hasMonumentProtection === false &&
      hasQualifyingPvConsumption(propertyData)
    );
  };

  // Prüfen, ob Lead disqualifiziert ist
  const isLeadDisqualified = () => {
    if (propertyData.isOwner === false) return true;
    if (propertyData.roofOrientation === 'Nord') return true;
    if (propertyData.hasMonumentProtection === true) return true;

    const effectiveArea = getEffectiveRoofArea();
    if (propertyData.roofAreaOver20 === false && propertyData.hasAlternativeMounting === false) return true;
    if (effectiveArea > 0 && effectiveArea < 20) return true;

    if (isPvConsumptionDisqualified(propertyData)) return true;

    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Kundendaten vom bestehenden Kunden verwenden
      const leadData = {
        userId: user.id,
        customerId: customerId, // WICHTIG: customerId explizit übergeben
        produkt: 'PV',
        name: customer?.name || '',
        nachname: customer?.nachname || '',
        email: customer?.email || '',
        handy: customer?.handy || '',
        strasse: customer?.strasse || '',
        hausnummer: customer?.hausnummer || '',
        postleitzahl: customer?.postleitzahl || '',
        stadt: customer?.stadt || '',
        anrede: customer?.anrede || '',
        dienstleister: customer?.dienstleister || user?.full_name || '',
        projekt: customer?.projekt || '',
        // PV-Immobiliendaten
        isOwner: propertyData.isOwner,
        buildingType: propertyData.buildingType,
        roofOrientation: propertyData.roofOrientation,
        roofAreaOver20: propertyData.roofAreaOver20,
        roofAreaSize: propertyData.roofAreaSize,
        hasAlternativeMounting: propertyData.hasAlternativeMounting,
        alternativeMountingArea: propertyData.alternativeMountingArea,
        hasMonumentProtection: propertyData.hasMonumentProtection,
        ...getPvConsumptionSubmitValues(propertyData),
        hasFutureEnergyConsumers: propertyData.hasFutureEnergyConsumers,
        isQualified: isLeadQualified(),
        customerStatus: customer?.status || 'Erfasst'
      };

      const leadResponse = await addLead(leadData);

      if (!leadResponse.success) {
        setError(leadResponse.error || 'Fehler beim Hinzufügen des PV-Leads');
        return;
      }

      setSuccess('PV-Lead erfolgreich hinzugefügt!');

      // Nach 2 Sekunden zurück zur Kundendetailseite
      setTimeout(() => {
        navigate(`/customer/${customerId}`);
      }, 2000);

    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Render-Funktion für PV-Daten
  const renderPvForm = () => {
    return (
      <div className="pv-form-step">
        <h2 className="form-title">PV Anlage hinzufügen</h2>
        {customer && (
          <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f0f0', borderRadius: '8px' }}>
            <strong>Kunde:</strong> {customer.anrede} {customer.name} {customer.nachname}
            <br />
            <strong>E-Mail:</strong> {customer.email || 'Keine E-Mail'}
            <br />
            <strong>Telefon:</strong> {customer.handy || 'Keine Telefonnummer'}
          </div>
        )}

        {/* Eigentümer */}
        <div className="form-section">
          <label className="form-label required">Eigentümer</label>
          <div className="yes-no-buttons">
            <button
              type="button"
              className={`yes-button ${propertyData.isOwner === true ? 'selected' : ''}`}
              onClick={() => handleOwnershipChange(true)}
            >
              <div className="icon-container">
                <svg viewBox="0 0 24 24" className="yes-icon">
                  <path
                    d="M4 12.6L9 17.6L20 6.6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span>Ja</span>
            </button>
            <button
              type="button"
              className={`no-button ${propertyData.isOwner === false ? 'selected' : ''}`}
              onClick={() => handleOwnershipChange(false)}
            >
              <div className="icon-container">
                <svg viewBox="0 0 24 24" className="no-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span>Nein</span>
            </button>
          </div>
          {propertyData.isOwner === false && (
            <div className="qualification-message disqualified">
              <svg viewBox="0 0 24 24" className="qualification-icon">
                <path
                  d="M5 5L19 19M5 19L19 5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Lead nicht Qualifiziert</span>
            </div>
          )}
        </div>

        {/* Gebäudeart */}
        {progression >= 2 && (
          <div className="form-section">
            <label className="form-label required">Gebäudeart</label>
            <div className="building-type-buttons">
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Einfamilienhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Einfamilienhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M50,20 L20,45 L20,80 L80,80 L80,45 Z" fill="none" stroke="currentColor" strokeWidth="3" />
                    <rect x="35" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="55" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="45" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <span>Einfamilienhaus</span>
              </button>
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Zweifamilienhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Zweifamilienhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M50,20 L15,45 L15,80 L85,80 L85,45 Z" fill="none" stroke="currentColor" strokeWidth="3" />
                    <line x1="50" y1="45" x2="50" y2="80" stroke="currentColor" strokeWidth="2" />
                    <rect x="25" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="65" y="50" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="30" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="60" y="65" width="10" height="15" fill="none" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <span>Zweifamilienhaus</span>
              </button>
              <button
                type="button"
                className={`building-button ${propertyData.buildingType === 'Reihenhaus' ? 'selected' : ''}`}
                onClick={() => handleBuildingTypeChange('Reihenhaus')}
              >
                <div className="building-icon">
                  <svg viewBox="0 0 100 100">
                    <path d="M10,80 L10,45 L30,30 L30,80 Z M30,80 L30,30 L50,30 L50,80 Z M50,80 L50,30 L70,30 L70,80 Z M70,80 L70,30 L90,45 L90,80 Z" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="15" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="37" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="56" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="77" y="60" width="8" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </div>
                <span>Reihenhaus</span>
              </button>
            </div>
          </div>
        )}

        {/* Dachausrichtung */}
        {progression >= 3 && (
          <div className="form-section">
            <label className="form-label required">Dachausrichtung</label>
            <div className="roof-orientation-buttons">
              <button
                type="button"
                className={`direction-button ${propertyData.roofOrientation === 'West' ? 'selected' : ''}`}
                onClick={() => handleRoofOrientationChange('West')}
              >
                <div className="direction-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M4 12h12M4 12l5 -5M4 12l5 5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>West</span>
              </button>
              <button
                type="button"
                className={`direction-button ${propertyData.roofOrientation === 'Ost' ? 'selected' : ''}`}
                onClick={() => handleRoofOrientationChange('Ost')}
              >
                <div className="direction-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 12H8M20 12l-5 -5M20 12l-5 5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>Ost</span>
              </button>
              <button
                type="button"
                className={`direction-button ${propertyData.roofOrientation === 'Nord' ? 'selected' : ''}`}
                onClick={() => handleRoofOrientationChange('Nord')}
              >
                <div className="direction-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 4v12M12 4l-5 5M12 4l5 5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>Nord</span>
              </button>
              <button
                type="button"
                className={`direction-button ${propertyData.roofOrientation === 'Süd' ? 'selected' : ''}`}
                onClick={() => handleRoofOrientationChange('Süd')}
              >
                <div className="direction-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 20V8M12 20l-5 -5M12 20l5 -5" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <span>Süd</span>
              </button>
            </div>
            {propertyData.roofOrientation === 'Nord' && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Dachfläche über 20QM */}
        {progression >= 4 && (
          <div className="form-section">
            <label className="form-label required">Dachfläche über 20QM</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button ${propertyData.roofAreaOver20 === true ? 'selected' : ''}`}
                onClick={() => handleRoofAreaOver20Change(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`yes-button ${propertyData.roofAreaOver20 === false ? 'selected' : ''}`}
                onClick={() => handleRoofAreaOver20Change(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
          </div>
        )}

        {/* Dachflächengröße */}
        {progression >= 5 && propertyData.roofAreaOver20 === true && (
          <div className="form-section">
            <label className="form-label required">Wie viele QM?</label>
            <input
              type="number"
              name="roofAreaSize"
              value={propertyData.roofAreaSize}
              onChange={handleRoofAreaSizeChange}
              className="form-input"
              placeholder="z.B. 40"
              required
            />
            {Number(propertyData.roofAreaSize) < 20 && propertyData.roofAreaSize !== '' && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Alternative Montage */}
        {progression >= 6 && propertyData.roofAreaOver20 === false && (
          <div className="form-section">
            <label className="form-label required">Alternative Montage möglich (Carport, Garage)?</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button ${propertyData.hasAlternativeMounting === true ? 'selected' : ''}`}
                onClick={() => handleAlternativeMountingChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`no-button ${propertyData.hasAlternativeMounting === false ? 'selected' : ''}`}
                onClick={() => handleAlternativeMountingChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            {propertyData.hasAlternativeMounting === false && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Alternative Montagefläche */}
        {progression >= 7 && propertyData.roofAreaOver20 === false && propertyData.hasAlternativeMounting === true && (
          <div className="form-section">
            <label className="form-label required">Wie viele QM alternative Montagefläche?</label>
            <input
              type="number"
              name="alternativeMountingArea"
              value={propertyData.alternativeMountingArea}
              onChange={handleAlternativeMountingAreaChange}
              className="form-input"
              placeholder="z.B. 25"
              required
            />
            {Number(propertyData.alternativeMountingArea) < 20 && propertyData.alternativeMountingArea !== '' && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        {/* Denkmalschutz */}
        {progression >= 8 && (
          <div className="form-section">
            <label className="form-label required">Denkmalschutz / Asbest</label>
            <div className="yes-no-buttons">
              <button
                type="button"
                className={`yes-button reverse-yes-button ${propertyData.hasMonumentProtection === true ? 'selected' : ''}`}
                onClick={() => handleMonumentProtectionChange(true)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="yes-icon">
                    <path
                      d="M4 12.6L9 17.6L20 6.6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Ja</span>
              </button>
              <button
                type="button"
                className={`reverse-no-button ${propertyData.hasMonumentProtection === false ? 'selected' : ''}`}
                onClick={() => handleMonumentProtectionChange(false)}
              >
                <div className="icon-container">
                  <svg viewBox="0 0 24 24" className="no-icon">
                    <path
                      d="M5 5L19 19M5 19L19 5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <span>Nein</span>
              </button>
            </div>
            {propertyData.hasMonumentProtection === true && (
              <div className="qualification-message disqualified">
                <svg viewBox="0 0 24 24" className="qualification-icon">
                  <path
                    d="M5 5L19 19M5 19L19 5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Lead nicht Qualifiziert</span>
              </div>
            )}
          </div>
        )}

        <PvConsumptionQuestions
          data={propertyData}
          progression={progression}
          onOver2700Change={handleConsumptionOver2700Change}
          onYearlyConsumptionChange={handleYearlyConsumptionChange}
          onRangeWith3MonthsChange={handleConsumptionInRangeWith3MonthsChange}
          onRealizationTimeframeChange={handleRealizationTimeframeChange}
          showQualifiedBanner
          isQualified={isLeadQualified()}
          isDisqualified={isLeadDisqualified()}
          isStepValid={isStep1Valid()}
        />

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={() => navigate(`/customer/${customerId}`)}>
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !isStep1Valid() || isLeadDisqualified()}
          >
            {loading ? 'Wird gespeichert...' : 'PV-Lead speichern'}
          </button>
        </div>
      </div>
    );
  };

  if (customerLoading) {
    return (
      <>
        <DashboardNav user={user} />
        <div style={{ padding: '20px', textAlign: 'center' }}>Lade Kundendaten...</div>
      </>
    );
  }

  if (!customerId) {
    return (
      <>
        <DashboardNav user={user} />
        <div style={{ padding: '20px', color: 'red' }}>Fehler: Keine Kunden-ID angegeben</div>
      </>
    );
  }

  if (!customer) {
    return (
      <>
        <DashboardNav user={user} />
        <div style={{ padding: '20px', color: 'red' }}>Fehler: Kunde nicht gefunden</div>
      </>
    );
  }

  return (
    <>
      <DashboardNav user={user} />
      <div className="lead-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          {renderPvForm()}
        </form>
      </div>
    </>
  );
}

export default AddPvLeadToCustomer;
